/**
 * BundlePlanCard - Redesigned with Quiz styling
 */
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RepeatIcon from "@mui/icons-material/Repeat";
import SpeedIcon from "@mui/icons-material/Speed";
import CancelIcon from "@mui/icons-material/Cancel";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type {
  VariantIndexItem,
  BundlePlan,
  RecipeGridItem,
} from "../utils/recharge";
import { QuizStyledCard } from "./shared/QuizStyledCard";
import { QuizButton } from "./shared/QuizButton";
import { QUIZ } from "@/theme/quizTokens";

function statusChip(status?: string) {
  const s = String(status || "unknown").toLowerCase();
  const label =
    s === "active"
      ? "Active"
      : s === "cancelled"
      ? "Cancelled"
      : s === "paused"
      ? "Paused"
      : s;

  // Quiz-style status colors
  const bgColor =
    s === "active"
      ? "#E5FFEA"
      : s === "paused"
      ? "#FFF0D6"
      : s === "cancelled"
      ? "#FFE5E2"
      : "#E6F0FF";

  const textColor =
    s === "active"
      ? "#2E7D32"
      : s === "paused"
      ? "#CE7A00"
      : s === "cancelled"
      ? "#D14C3F"
      : "#1A57D3";

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        background: bgColor,
        color: textColor,
        fontWeight: 700,
        textTransform: "capitalize",
        height: 28,
        "& .MuiChip-label": { px: 1.5, fontSize: 12 },
      }}
    />
  );
}

export function BundlePlanCard({
  sub,
  plan,
  boxInfo,
  recipes,
  treats,
  onEditMeals,
  onChangeRecipes,
  onChangeFrequency,
  onChangeDailyServing,
  onStopPlan,
}: {
  sub: any;
  plan: BundlePlan;
  boxInfo: VariantIndexItem | null;
  recipes: RecipeGridItem[];
  treats: { variantId: string; qty: number; info: VariantIndexItem | null }[];
  onEditMeals: () => void;
  onChangeRecipes?: () => void;
  onChangeFrequency?: () => void;
  onChangeDailyServing?: () => void;
  onStopPlan?: () => void;
}) {
  // Management action items (delivery actions are now global)
  const managementActions = [
    { label: "Change recipes", icon: MenuBookIcon, onClick: onChangeRecipes || onEditMeals, color: QUIZ.color.brown },
    { label: "Change delivery frequency", icon: RepeatIcon, onClick: onChangeFrequency, color: "#CE7A00" },
    { label: "Change daily serving", icon: SpeedIcon, onClick: onChangeDailyServing, color: "#9C27B0" },
    { label: "Stop Plan", icon: CancelIcon, onClick: onStopPlan, color: "#D14C3F" },
  ];

  console.log('Recipes in BundlePlanCard:', recipes);
  return (
    <QuizStyledCard variant="white">
      {/* Top row with box image and details */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1.5, md: 2 },
          alignItems: "flex-start",
          mb: { xs: 2, md: 2.5 },
        }}
      >
        {/* Box Image */}
        <Box
          sx={{
            width: { xs: 56, md: 80 },
            height: { xs: 56, md: 80 },
            borderRadius: { xs: "12px", md: "16px" },
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,.10)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          {boxInfo?.imageUrl ? (
            <Box
              component="img"
              src={boxInfo.imageUrl}
              alt={plan.productTitle}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RestaurantIcon sx={{ fontSize: 32, color: QUIZ.color.brown, opacity: 0.4 }} />
            </Box>
          )}
        </Box>

        {/* Plan Details & Price */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                <Typography
                  sx={{
                    fontSize: { xs: 16, md: 20 },
                    fontWeight: 800,
                    color: QUIZ.color.brown,
                    lineHeight: 1.2,
                  }}
                >
                  {plan.productTitle || "Subscription"}
                </Typography>
                {statusChip(sub?.status)}
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: 12, md: 14 },
                  color: QUIZ.color.brown,
                  opacity: 0.65,
                }}
              >
                {plan.packSizeGrams}g packs • Every {(() => {
                  const freq = Number(sub?.order_interval_frequency || 1);
                  const unit = String(sub?.order_interval_unit || "day").toLowerCase();
                  // Convert weeks to days for display
                  if (unit === "week" || unit === "weeks") {
                    const days = freq * 7;
                    return `${days} day${days > 1 ? "s" : ""}`;
                  }
                  return `${freq} ${unit}${freq > 1 ? "s" : ""}`;
                })()}
              </Typography>
            </Box>

            {/* Price */}
            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: { xs: 20, md: 28 },
                  fontWeight: 900,
                  color: QUIZ.color.brown,
                  lineHeight: 1,
                }}
              >
                £{Number(sub?.price || 0).toFixed(2)}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 10, md: 12 },
                  color: QUIZ.color.brown,
                  opacity: 0.5,
                  mt: 0.25,
                }}
              >
                per delivery
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: { xs: 2, md: 2.5 } }} />

      {/* Management Actions Grid */}
      <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
        <Typography
          sx={{
            fontSize: { xs: 16, md: 18 },
            fontWeight: 800,
            color: QUIZ.color.brown,
            mb: 1.5,
          }}
        >
          Manage Subscription
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1,
          }}
        >
          {managementActions.map((action) => {
            const IconComponent = action.icon;
            return (
              <Box
                key={action.label}
                onClick={action.onClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: { xs: 1.25, md: 1.5 },
                  borderRadius: "12px",
                  border: "1px solid rgba(0,0,0,.08)",
                  background: QUIZ.color.cream,
                  cursor: action.onClick ? "pointer" : "default",
                  opacity: action.onClick ? 1 : 0.5,
                  transition: "all 150ms ease",
                  "&:hover": action.onClick ? {
                    background: "#F5F0E8",
                    borderColor: "rgba(0,0,0,.12)",
                    transform: "translateY(-1px)",
                  } : {},
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    background: `${action.color}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <IconComponent sx={{ fontSize: 20, color: action.color }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: 13, md: 14 },
                        fontWeight: 700,
                        color: QUIZ.color.brown,
                      }}
                      noWrap
                    >
                      {action.label}
                    </Typography>
                  </Box>
                </Box>
                <ChevronRightIcon sx={{ fontSize: 20, color: QUIZ.color.brown, opacity: 0.3 }} />
              </Box>
            );
          })}
        </Box>
      </Box>

      <Divider sx={{ mb: { xs: 2.5, md: 3 } }} />

      {/* Recipes Section */}
      <Box>
        <Typography
          sx={{
            fontSize: { xs: 18, md: 20 },
            fontWeight: 800,
            color: QUIZ.color.brown,
            mb: 0.5,
          }}
        >
          Recipes in your box
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 12, md: 13 },
            color: QUIZ.color.brown,
            opacity: 0.65,
            mb: 2,
          }}
        >
          Selected recipes are highlighted. Other available recipes are greyed
          out.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(5, 1fr)",
            },
            gap: { xs: 1.5, md: 2 },
          }}
        >
          {recipes.map((r) => (
            <Box
              key={r.variantId}
              sx={{
                position: "relative",
                borderRadius: { xs: "16px", md: "20px" },
                overflow: "hidden",
                background: r.selected
                  ? "linear-gradient(145deg, #FFF7F3 0%, #FFE8E5 100%)"
                  : "#F0EBE6",
                border: r.selected
                  ? "2px solid #4A3026"
                  : "2px dashed #D4C4B5",
                transition: "all 250ms ease",
                transform: r.selected ? "scale(1)" : "scale(0.98)",
                opacity: r.selected ? 1 : 0.7,
                "&:hover": r.selected ? {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 24px rgba(123,51,6,0.15)",
                } : {},
              }}
            >
              {/* Quantity Badge - Top right corner, bone-shaped feel */}
              {r.selected && r.count > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 10,
                    minWidth: { xs: 28, md: 34 },
                    height: { xs: 28, md: 34 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "10px",
                    background: QUIZ.color.brown,
                    color: "#FFF7F3",
                    fontSize: { xs: 14, md: 16 },
                    fontWeight: 900,
                    fontFamily: QUIZ.font.display,
                    boxShadow: "0 4px 12px rgba(123,51,6,0.4)",
                    border: "2px solid #E9DFD7",
                  }}
                >
                  ×{r.count}
                </Box>
              )}

              {/* Image with warm overlay */}
              <Box sx={{ position: "relative" }}>
                {r.imageUrl ? (
                  <Box sx={{ position: "relative" }}>
                    <Box
                      component="img"
                      src={r.imageUrl}
                      alt={r.productTitle}
                      sx={{
                        width: "100%",
                        height: { xs: 80, md: 95 },
                        objectFit: "cover",
                        display: "block",
                        filter: r.selected
                          ? "saturate(0.85) brightness(1.02)"
                          : "grayscale(100%) brightness(0.9)",
                      }}
                    />
                    {/* Warm gradient overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: r.selected
                          ? "linear-gradient(180deg, rgba(250,245,235,0.1) 0%, rgba(255,225,221,0.25) 100%)"
                          : "rgba(240,235,230,0.5)",
                        pointerEvents: "none",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: { xs: 80, md: 95 },
                      background: r.selected
                        ? "linear-gradient(135deg, #FAF5EB 0%, #E9DFD7 100%)"
                        : "#E8E3DE",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <RestaurantIcon sx={{ fontSize: 28, color: QUIZ.color.brown, opacity: 0.3 }} />
                  </Box>
                )}

                {/* "Not in box" badge for unselected */}
                {!r.selected && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "8px",
                      background: "rgba(74, 48, 38,0.85)",
                      color: "#FFF7F3",
                      fontSize: { xs: 9, md: 10 },
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Not in box
                  </Box>
                )}
              </Box>

              {/* Recipe name footer */}
              <Box
                sx={{
                  p: { xs: 1, md: 1.25 },
                  background: r.selected
                    ? "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 100%)"
                    : "transparent",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontFamily: QUIZ.font.display,
                    fontSize: { xs: 11, md: 12 },
                    color: r.selected ? QUIZ.color.brown : "#8B7B6B",
                    lineHeight: 1.25,
                    textAlign: "center",
                  }}
                  noWrap
                >
                  {r.productTitle}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 10, md: 11 },
                    color: r.selected ? QUIZ.color.brown : "#A89888",
                    mt: 0.25,
                    textAlign: "center",
                    fontWeight: 600,
                  }}
                  noWrap
                >
                  {r.variantTitle}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Treats/Add-ons Section */}
      {treats.length > 0 && (
        <>
          <Divider sx={{ my: { xs: 2.5, md: 3 } }} />
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 20 },
                fontWeight: 800,
                color: QUIZ.color.brown,
                mb: 2,
              }}
            >
              Add-ons & Treats
            </Typography>

            <Stack spacing={1.5}>
              {treats.map((t) => (
                <Box
                  key={t.variantId}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    border: "1px solid rgba(0,0,0,.08)",
                    borderRadius: { xs: "12px", md: "14px" },
                    p: { xs: 1.25, md: 1.5 },
                    background: QUIZ.color.cream,
                  }}
                >
                  {/* Treat Image */}
                  <Box
                    sx={{
                      width: { xs: 48, md: 56 },
                      height: { xs: 48, md: 56 },
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: "1px solid rgba(0,0,0,.10)",
                      background: QUIZ.color.cream,
                      flexShrink: 0,
                    }}
                  >
                    {t.info?.imageUrl ? (
                      <Box
                        component="img"
                        src={t.info.imageUrl}
                        alt={t.info.productTitle}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : null}
                  </Box>

                  {/* Treat Info */}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: 14, md: 15 },
                        color: QUIZ.color.brown,
                      }}
                      noWrap
                    >
                      {t.info?.productTitle || "Add-on"}
                    </Typography>
                    <Typography
                      sx={{
                        opacity: 0.65,
                        fontSize: { xs: 12, md: 13 },
                        color: QUIZ.color.brown,
                        mt: 0.25,
                      }}
                      noWrap
                    >
                      {t.info?.variantTitle || ""}
                    </Typography>
                  </Box>

                  {/* Quantity */}
                  <Chip
                    label={`×${t.qty}`}
                    size="small"
                    sx={{
                      background: "#E5FFEA",
                      color: "#2E7D32",
                      fontWeight: 800,
                      height: 28,
                      "& .MuiChip-label": { px: 1.5, fontSize: 13 },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        </>
      )}
    </QuizStyledCard>
  );
}
